import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Copy,
  Search,
  Pencil,
  Trash2,
  Package,
  TrendingUp,
  Plus,
  X,
  CheckCircle2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'general', label: 'General' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'plumbing', label: 'Plumbing' },
  { key: 'hvac', label: 'HVAC' },
  { key: 'mechanical', label: 'Mechanical' },
];

const CATEGORY_STYLES = {
  electrical: 'bg-amber-100 text-amber-800 border-amber-400',
  plumbing: 'bg-blue-100 text-blue-800 border-blue-400',
  hvac: 'bg-green-100 text-green-800 border-green-400',
  mechanical: 'bg-red-100 text-red-800 border-red-400',
  general: 'bg-zinc-100 text-zinc-700 border-zinc-400',
};

const formatCurrency = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const emptyLineItem = () => ({ _key: `li-${Math.random().toString(36).slice(2, 10)}`, product_description: '', quantity: 1, unit: 'each', unit_price: 0 });

const TemplatesPage = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'Owner' || user?.role === 'Project Manager';

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editing, setEditing] = useState(null); // template copy being edited (null = closed)
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // template to delete

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/templates/list`, { withCredentials: true });
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => {
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        (t.name || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.vendor_name || '').toLowerCase().includes(q)
      );
    });
  }, [templates, search, categoryFilter]);

  const stats = useMemo(() => {
    const total = templates.length;
    const custom = templates.filter(t => !t.is_preset).length;
    const totalUsage = templates.reduce((s, t) => s + (t.usage_count || 0), 0);
    return { total, custom, totalUsage };
  }, [templates]);

  const openEdit = (tmpl) => {
    setEditing({
      ...tmpl,
      line_items: (tmpl.line_items || []).map(li => ({
        _key: li.line_item_id || `li-${Math.random().toString(36).slice(2, 10)}`,
        product_description: li.product_description || '',
        quantity: li.quantity || 0,
        unit: li.unit || 'each',
        unit_price: li.unit_price || 0,
      })),
      tax: tmpl.tax || 0,
      freight: tmpl.freight || 0,
    });
  };

  const updateEditField = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
  };

  const updateEditLineItem = (idx, field, value) => {
    setEditing(prev => ({
      ...prev,
      line_items: prev.line_items.map((li, i) => i === idx ? { ...li, [field]: value } : li),
    }));
  };

  const addEditLineItem = () => {
    setEditing(prev => ({ ...prev, line_items: [...prev.line_items, emptyLineItem()] }));
  };

  const removeEditLineItem = (idx) => {
    setEditing(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx),
    }));
  };

  const editSubtotal = useMemo(() => {
    if (!editing) return 0;
    return editing.line_items.reduce(
      (s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0),
      0
    );
  }, [editing]);

  const editTotal = editSubtotal + (parseFloat(editing?.tax) || 0) + (parseFloat(editing?.freight) || 0);

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) {
      toast.error('Template name is required');
      return;
    }
    setSaving(true);
    try {
      const validItems = editing.line_items
        .filter(li => li.product_description?.trim())
        .map(li => ({
          product_description: li.product_description.trim(),
          quantity: parseFloat(li.quantity) || 0,
          unit: li.unit || 'each',
          unit_price: parseFloat(li.unit_price) || 0,
        }));

      const response = await axios.put(
        `${API}/orders/templates/${editing.template_id}`,
        {
          name: editing.name.trim(),
          description: editing.description?.trim() || null,
          category: editing.category || 'general',
          vendor_name: editing.vendor_name?.trim() || null,
          line_items: validItems,
          tax: parseFloat(editing.tax) || 0,
          freight: parseFloat(editing.freight) || 0,
        },
        { withCredentials: true }
      );
      setTemplates(prev => prev.map(t => t.template_id === editing.template_id ? response.data : t));
      toast.success('Template updated');
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update template');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/orders/templates/${confirmDelete.template_id}`, { withCredentials: true });
      setTemplates(prev => prev.filter(t => t.template_id !== confirmDelete.template_id));
      toast.success('Template deleted');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="templates-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wide flex items-center gap-3">
          <Copy className="w-7 h-7 text-primary" />
          Order Templates
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Manage reusable order templates. Save time on recurring orders.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="bg-white border-2 border-black p-3 sm:p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-total">
          <p className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Templates</p>
          <p className="font-mono text-2xl sm:text-3xl font-bold text-zinc-900">{stats.total}</p>
        </div>
        <div className="bg-white border-2 border-black p-3 sm:p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-custom">
          <p className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Custom</p>
          <p className="font-mono text-2xl sm:text-3xl font-bold text-primary">{stats.custom}</p>
        </div>
        <div className="bg-white border-2 border-black p-3 sm:p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-usage">
          <p className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Uses</p>
          <p className="font-mono text-2xl sm:text-3xl font-bold text-emerald-600">{stats.totalUsage}</p>
        </div>
      </div>

      {/* Search + filters */}
      <div className="bg-white border border-zinc-200 p-3 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search templates, vendors, descriptions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="templates-search"
            className="w-full h-10 pl-10 pr-3 border border-zinc-200 bg-white text-sm focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto" data-testid="category-filter">
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              onClick={() => setCategoryFilter(c.key)}
              data-testid={`filter-${c.key}`}
              className={`h-10 px-3 text-[11px] font-heading font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${
                categoryFilter === c.key
                  ? 'bg-primary text-zinc-900 border-2 border-black'
                  : 'bg-white text-zinc-500 border border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Template grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-48 bg-white border border-zinc-200 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <Package className="w-16 h-16 mx-auto mb-3 text-zinc-300" />
          <p className="text-lg font-heading font-bold uppercase mb-1">No templates found</p>
          <p className="text-sm text-zinc-500">
            {search || categoryFilter !== 'all'
              ? 'Try a different search or category'
              : 'Open any order and tap "Save as Template" to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {filtered.map(t => {
            const catStyle = CATEGORY_STYLES[t.category] || CATEGORY_STYLES.general;
            return (
              <div
                key={t.template_id}
                className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-4 flex flex-col"
                data-testid={`template-card-${t.template_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold uppercase text-base tracking-wide truncate">{t.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className={`px-2 py-0.5 text-[9px] font-heading font-bold uppercase border ${catStyle}`}>
                        {t.category}
                      </span>
                      {t.is_preset && (
                        <span className="px-2 py-0.5 text-[9px] font-heading font-bold uppercase bg-primary text-zinc-900 border border-black">
                          Preset
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="font-mono text-lg font-bold text-zinc-900 whitespace-nowrap">
                    {formatCurrency(t.total)}
                  </p>
                </div>

                {t.description && (
                  <p className="text-xs text-zinc-500 mb-2 line-clamp-2">{t.description}</p>
                )}

                {t.vendor_name && (
                  <p className="text-[11px] text-zinc-500 mb-2 font-mono truncate">
                    <span className="text-zinc-400">Vendor:</span> {t.vendor_name}
                  </p>
                )}

                <div className="flex items-center gap-3 text-[11px] text-zinc-500 mb-3 font-mono border-t border-b border-zinc-100 py-2">
                  <span className="flex items-center gap-1">
                    <Package className="w-3 h-3" /> {t.line_items?.length || 0} items
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {t.usage_count || 0} uses
                  </span>
                </div>

                {canEdit && !t.is_preset && (
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => openEdit(t)}
                      data-testid={`edit-template-${t.template_id}`}
                      className="flex-1 h-9 bg-zinc-900 text-white font-heading font-bold uppercase tracking-wider text-[11px] border border-black hover:bg-zinc-800 flex items-center justify-center gap-1"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(t)}
                      data-testid={`delete-template-${t.template_id}`}
                      className="h-9 px-3 bg-white text-destructive font-heading font-bold uppercase tracking-wider text-[11px] border border-black hover:bg-destructive hover:text-white flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {t.is_preset && (
                  <p className="text-[10px] text-zinc-400 italic mt-auto pt-2 border-t border-zinc-100">
                    Preset templates are read-only
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl border border-zinc-200 rounded-none p-0 max-h-[90vh] overflow-hidden flex flex-col" data-testid="edit-template-dialog">
          <DialogHeader className="p-5 pb-0 flex-shrink-0">
            <DialogTitle className="text-xl font-heading font-bold uppercase tracking-wide">Edit Template</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="p-5 pt-3 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Name</label>
                  <input
                    type="text"
                    value={editing.name || ''}
                    onChange={(e) => updateEditField('name', e.target.value)}
                    data-testid="edit-name-input"
                    className="w-full h-10 px-3 border-2 border-black bg-white text-sm font-medium focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Category</label>
                  <select
                    value={editing.category || 'general'}
                    onChange={(e) => updateEditField('category', e.target.value)}
                    data-testid="edit-category-select"
                    className="w-full h-10 px-3 border-2 border-black bg-white text-sm font-bold uppercase"
                  >
                    {CATEGORIES.filter(c => c.key !== 'all').map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Vendor <span className="text-zinc-400 normal-case">(optional)</span></label>
                <input
                  type="text"
                  value={editing.vendor_name || ''}
                  onChange={(e) => updateEditField('vendor_name', e.target.value)}
                  data-testid="edit-vendor-input"
                  className="w-full h-10 px-3 border-2 border-black bg-white text-sm"
                  placeholder="e.g. Ferguson Enterprises"
                />
              </div>

              <div>
                <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Description</label>
                <textarea
                  value={editing.description || ''}
                  onChange={(e) => updateEditField('description', e.target.value)}
                  data-testid="edit-description-input"
                  rows={2}
                  className="w-full px-3 py-2 border-2 border-black bg-white text-sm focus:outline-none focus:border-primary resize-none"
                />
              </div>

              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">
                    Line Items
                  </label>
                  <button
                    onClick={addEditLineItem}
                    data-testid="add-line-item-btn"
                    className="h-7 px-2 bg-zinc-900 text-white text-[10px] font-heading font-bold uppercase tracking-wider hover:bg-zinc-800 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {editing.line_items.map((li, idx) => (
                    <div key={li._key || idx} className="grid grid-cols-12 gap-1 items-center" data-testid={`edit-line-item-${idx}`}>
                      <input
                        type="text"
                        placeholder="Description"
                        value={li.product_description}
                        onChange={(e) => updateEditLineItem(idx, 'product_description', e.target.value)}
                        className="col-span-5 h-9 px-2 border border-zinc-200 text-xs focus:outline-none focus:border-primary"
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={li.quantity}
                        onChange={(e) => updateEditLineItem(idx, 'quantity', e.target.value)}
                        className="col-span-2 h-9 px-2 border border-zinc-200 text-xs font-mono"
                      />
                      <input
                        type="text"
                        placeholder="Unit"
                        value={li.unit}
                        onChange={(e) => updateEditLineItem(idx, 'unit', e.target.value)}
                        className="col-span-2 h-9 px-2 border border-zinc-200 text-xs"
                      />
                      <input
                        type="number"
                        placeholder="Price"
                        value={li.unit_price}
                        onChange={(e) => updateEditLineItem(idx, 'unit_price', e.target.value)}
                        className="col-span-2 h-9 px-2 border border-zinc-200 text-xs font-mono"
                      />
                      <button
                        onClick={() => removeEditLineItem(idx)}
                        className="col-span-1 h-9 flex items-center justify-center text-zinc-400 hover:text-red-500"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Tax</label>
                  <input
                    type="number"
                    value={editing.tax}
                    onChange={(e) => updateEditField('tax', e.target.value)}
                    data-testid="edit-tax-input"
                    className="w-full h-10 px-3 border border-zinc-200 bg-white text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 mb-1">Freight</label>
                  <input
                    type="number"
                    value={editing.freight}
                    onChange={(e) => updateEditField('freight', e.target.value)}
                    data-testid="edit-freight-input"
                    className="w-full h-10 px-3 border border-zinc-200 bg-white text-sm font-mono"
                  />
                </div>
              </div>

              <div className="bg-zinc-50 border border-zinc-200 p-3 flex justify-between items-center">
                <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Total</span>
                <span className="font-mono text-lg font-bold">{formatCurrency(editTotal)}</span>
              </div>
            </div>
          )}
          {editing && (
            <div className="p-5 pt-0 border-t border-zinc-200 flex gap-2 flex-shrink-0">
              <button
                onClick={() => setEditing(null)}
                className="flex-1 h-10 bg-white text-zinc-900 font-bold uppercase border-2 border-black hover:bg-zinc-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editing.name?.trim()}
                data-testid="save-edit-btn"
                className="flex-1 h-10 bg-primary text-zinc-900 font-bold uppercase border-2 border-black hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.name}" will be permanently removed. Orders already created from this
              template are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TemplatesPage;
